import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './IncluirJogadoresRodada.css';

/**
 * Página para Admin incluir jogadores na rodada
 * Agora com seleção múltipla usando checkboxes (similar ao rateio da janta)
 */

const IncluirJogadoresRodada = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rodadaAberta, setRodadaAberta] = useState(null);
  const [jogadores, setJogadores] = useState([]);
  const [jogadoresConfirmados, setJogadoresConfirmados] = useState([]);
  const [jogadoresSelecionados, setJogadoresSelecionados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarDados();
  }, [isAdmin, navigate]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const [rodadasRes, jogadoresRes] = await Promise.all([
        apiClient.get('/rodadas'),
        apiClient.get('/jogadores'),
      ]);

      const rodadas = Array.isArray(rodadasRes.data) ? rodadasRes.data : [];
      const aberta = rodadas.find(r => r.status === 'aberta');

      if (!aberta) {
        setError('Não há rodada aberta. Abra uma rodada primeiro.');
        setLoading(false);
        return;
      }

      setRodadaAberta(aberta);

      // Carregar confirmações
      const confirmacoesRes = await apiClient.get(`/rodadas/${aberta.id}/confirmacoes`);
      const confirmacoes = Array.isArray(confirmacoesRes.data) ? confirmacoesRes.data : [];
      setJogadoresConfirmados(confirmacoes);

      // Filtrar jogadores ativos ou admins
      const jogadoresList = Array.isArray(jogadoresRes.data) ? jogadoresRes.data : [];
      setJogadores(jogadoresList.filter(j => j && (j.ativo || j.is_admin)));

      // Resetar seleção
      setJogadoresSelecionados([]);

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleJogador = (jogadorId) => {
    if (jogadoresSelecionados.includes(jogadorId)) {
      setJogadoresSelecionados(jogadoresSelecionados.filter(id => id !== jogadorId));
    } else {
      setJogadoresSelecionados([...jogadoresSelecionados, jogadorId]);
    }
  };

  const handleSelecionarTodos = () => {
    const jogadoresDisponiveis = jogadores
      .filter(j => !jogadoresConfirmados.includes(j.id))
      .map(j => j.id);
    
    if (jogadoresSelecionados.length === jogadoresDisponiveis.length) {
      setJogadoresSelecionados([]);
    } else {
      setJogadoresSelecionados(jogadoresDisponiveis);
    }
  };

  const handleIncluirJogadores = async (e) => {
    e.preventDefault();

    if (jogadoresSelecionados.length === 0) {
      setError('Selecione pelo menos um jogador para incluir');
      return;
    }

    try {
      setSalvando(true);
      setError('');
      setSuccess('');

      const response = await apiClient.post(`/rodadas/${rodadaAberta.id}/incluir-jogadores`, {
        jogadores_ids: jogadoresSelecionados,
      });

      const { resultados } = response.data;
      
      let mensagem = `✅ ${resultados.incluidos.length} jogador(es) incluído(s) com sucesso!`;
      
      if (resultados.ja_estavam.length > 0) {
        mensagem += `\n⚠️ ${resultados.ja_estavam.length} jogador(es) já estava(m) na rodada.`;
      }
      
      if (resultados.erros.length > 0) {
        mensagem += `\n❌ ${resultados.erros.length} erro(s) ao incluir jogador(es).`;
      }

      setSuccess(mensagem);
      
      // Recarregar dados
      await carregarDados();
      
      // Limpar seleção após 2 segundos
      setTimeout(() => {
        setSuccess('');
      }, 3000);

    } catch (err) {
      console.error('Erro ao incluir jogadores:', err);
      setError(err.response?.data?.error || 'Erro ao incluir jogadores');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodadaAberta) {
    return (
      <div className="container">
        <div className="error">Não há rodada aberta. Abra uma rodada primeiro.</div>
        <button onClick={() => navigate('/rodadas')} className="btn btn-primary">
          Ir para Rodadas
        </button>
      </div>
    );
  }

  const jogadoresNaoConfirmados = jogadores.filter(j => 
    !jogadoresConfirmados.some(c => c.jogador_id === j.id)
  );

  return (
    <div className="container incluir-jogadores-container">
      <div className="page-header">
        <h1>👥 Incluir Jogadores na Rodada</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      <div className="rodada-info">
        <p><strong>Rodada:</strong> {new Date(rodadaAberta.data).toLocaleDateString('pt-BR')}</p>
        <p><strong>Status:</strong> {rodadaAberta.status}</p>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success" style={{ whiteSpace: 'pre-line' }}>{success}</div>}

      <div className="jogadores-section">
        <h2>Jogadores Confirmados ({jogadoresConfirmados.length})</h2>
        {jogadoresConfirmados.length > 0 ? (
          <div className="jogadores-list">
            {jogadoresConfirmados.map(c => {
              const jogador = jogadores.find(j => j.id === c.jogador_id);
              return jogador ? (
                <div key={c.jogador_id} className="jogador-card confirmado">
                  <div className="jogador-info">
                    <strong>{jogador.nome}</strong>
                    <span className="badge-confirmado">✓ Confirmado</span>
                  </div>
                </div>
              ) : null;
            })}
          </div>
        ) : (
          <p className="sem-jogadores">Nenhum jogador confirmado ainda.</p>
        )}
      </div>

      <form onSubmit={handleIncluirJogadores} className="incluir-jogadores-form">
        <div className="jogadores-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2>Jogadores Disponíveis ({jogadoresNaoConfirmados.length})</h2>
            {jogadoresNaoConfirmados.length > 0 && (
              <button
                type="button"
                onClick={handleSelecionarTodos}
                className="btn btn-secondary"
                style={{ padding: '5px 15px', fontSize: '14px' }}
              >
                {jogadoresSelecionados.length === jogadoresNaoConfirmados.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
              </button>
            )}
          </div>
          
          {jogadoresNaoConfirmados.length > 0 ? (
            <div className="jogadores-checklist">
              {jogadoresNaoConfirmados.map(jogador => (
                <label key={jogador.id} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={jogadoresSelecionados.includes(jogador.id)}
                    onChange={() => handleToggleJogador(jogador.id)}
                  />
                  <span>{jogador.nome}</span>
                  {jogador.email && <span className="jogador-email">{jogador.email}</span>}
                </label>
              ))}
            </div>
          ) : (
            <p className="sem-jogadores">Todos os jogadores já estão confirmados.</p>
          )}
          
          <small>
            {jogadoresSelecionados.length > 0 
              ? `${jogadoresSelecionados.length} jogador(es) selecionado(s)` 
              : 'Selecione um ou mais jogadores para incluir na rodada'}
          </small>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            disabled={salvando || jogadoresSelecionados.length === 0} 
            className="btn btn-primary"
          >
            {salvando ? 'Incluindo...' : `Incluir ${jogadoresSelecionados.length > 0 ? `${jogadoresSelecionados.length} ` : ''}Jogador(es)`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IncluirJogadoresRodada;
